import { Color } from '@coast/schemas';
import { isNil } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableChildrenFn,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { Easing, StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { RCSubMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBAnimation } from '@shared/components/animation';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { Switch } from '@shared/components/form';
import { MBIconV2 } from '@shared/components/image';
import { MBScrollView } from '@shared/components/scrollview';
import { TextInput } from '@shared/components/textInput';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { MBTouchableOpacity } from '@shared/components/touchable';
import {
  MBTextBody1,
  MBTextBody2,
  MBTextOverlineBold,
  MBTypographyStyles,
} from '@shared/components/typography';
import { MBMenuRow } from '@shared/scenes/cards/bulk/components/menuRow';
import {
  defaultTagGroups,
  defaultTagGroupsForChecklists,
} from '@shared/scenes/channel/fragmentTemplates/contexts/tagOptions';
import { AuditTagComponent, TagComponent } from '@shared/scenes/workflows/config';
import { isAuditTagComponent, isTagComponent } from '@shared/scenes/workflows/utils';
import { MBSingleSelect } from '@shared/scenes/workflows/views/components/singleSelect';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { CHECKLIST_IN_LIST_VIEW, useFeatureFlag } from '@shared/util/featureFlags';
import { i18n } from '@shared/util/i18n';
import { useSetComponentField, useTagOptions } from '../../hooks';
import { TagOptionRow } from './TagOptionRow';
import { ComponentDefaultItemRow } from './componentDefaults/row/ComponentDefaultItemRow';
import { DefaultSelection } from './componentDefaults/selection/DefaultSelection';
import { MBWorkflowDefaultComponentOptions } from './defaultOptions';
import { Divider } from './divider';
import styles from './styles.module.css';
interface Props {
  workflowTemplateId: string;
  component: TagComponent | AuditTagComponent;
}

const cursorPointerStyle = { cursor: 'pointer' } as StyleProp<ViewStyle>;

export function MBWorkflowTagOptions({ workflowTemplateId, component }: Props) {
  const colors = useContext(ColorThemeContext);
  const [showArchived, setShowArchived] = useState(false);
  const isChecklistInListViewEnabled =
    useFeatureFlag(CHECKLIST_IN_LIST_VIEW) && !isAuditTagComponent(component);
  const isAuditTag = component.type === 'AUDIT_TAG';

  const [addAdditionalValues, setAddAdditionalValues] = useSetComponentField<
    boolean,
    TagComponent | AuditTagComponent
  >(component, 'addAdditionalValues');

  const {
    onAddOption,
    onUpdateOption,
    onArchiveOption,
    onUnarchiveOption,
    onReorderOption,
    onSetTagGroup,
    onCheckToggle,
    canArchiveOption,
    canAddOption,
    options,
    archivedOptions,
    checkedOn,
    checkedOff,
    onChecklistEnabledToggle,
  } = useTagOptions(component);

  const areChecklistOptionsSatisfied = options.length === 2;
  const isChecklistEnabled = isTagComponent(component) && component.checklistEnabled;
  const tagGroups = isChecklistEnabled ? defaultTagGroupsForChecklists : defaultTagGroups;

  const onDragEnd = useCallback<OnDragEndResponder>(
    result => {
      if (!result.destination) {
        return;
      }

      onReorderOption(result.source.index, result.destination.index);
    },
    [onReorderOption],
  );

  const renderDraggableOptionRow = useCallback<DraggableChildrenFn>(
    (provided, snapshot, rubric) => {
      const { index } = rubric.source;
      const option = options[index];
      const style = {
        ...provided.draggableProps.style,
        ...(snapshot.isDragging && {
          backgroundColor: colors.background,
          boxShadow: '2px 2px 10px rgba(0,0,0,0.2)',
          borderRadius: grid(1),
          opacity: snapshot.isDragging ? 0.5 : 1,
        }),
      };
      return (
        <div
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={style}
          ref={provided.innerRef}
        >
          <TagOptionRow
            tagForegroundColor={colors.foregroundActive}
            tagBackgroundColor={Colors.v2[option.backgroundColor ?? 'tagGray']}
            onPressArchive={canArchiveOption ? () => onArchiveOption(option.value) : undefined}
            onCheckToggle={onCheckToggle}
            isChecklistEnabled={isChecklistEnabled ?? false}
            isCheckedOn={checkedOn === option.value}
            isCheckedOff={checkedOff === option.value}
          >
            <View fill paddingHorizontal={1}>
              <TextInput
                autoFocus={index === options.length - 1}
                value={option.label}
                onChangeText={newLabel => onUpdateOption(option.value, newLabel)}
                style={{
                  backgroundColor: colors.backgroundInactive,
                  borderRadius: 8,
                  padding: grid(1),
                }}
                onKeyPress={({ nativeEvent }) => {
                  if (nativeEvent.key === 'Enter' && canAddOption) {
                    onAddOption();
                  }
                }}
              />
            </View>
          </TagOptionRow>
        </div>
      );
    },
    [
      canArchiveOption,
      canAddOption,
      colors,
      onAddOption,
      onArchiveOption,
      onCheckToggle,
      onUpdateOption,
      options,
      checkedOn,
      checkedOff,
      isChecklistEnabled,
    ],
  );

  const tagOptionsPreview = useMemo(() => {
    const MAX_TAGS = 3;
    const tagsToShow = options.slice(0, MAX_TAGS).filter(({ label }) => label);
    const extraTagsText = options.length > MAX_TAGS ? `+ ${options.length - MAX_TAGS}` : null;
    return (
      <>
        <Tags tags={tagsToShow} />
        {!isNil(extraTagsText) && (
          <View centerVertically>
            <MBTextBody2>{extraTagsText}</MBTextBody2>
          </View>
        )}
      </>
    );
  }, [options]);

  const onSelectTagGroup = useCallback(
    (tagGroup: string) => {
      const tagOptions = tagGroups[tagGroup];
      onSetTagGroup(tagOptions.options);
    },
    [onSetTagGroup, tagGroups],
  );

  const tagGroupSelector = ({ onFinishSelectGroup }: { onFinishSelectGroup(): void }) => {
    return (
      <MBSingleSelect
        title={i18n('workflowTemplate.tagSetOptions')}
        options={Object.keys(tagGroups)}
        onSelectOption={option => {
          onSelectTagGroup(option);
          onFinishSelectGroup();
        }}
        getLabel={tagGroup => {
          const tags = tagGroups[tagGroup]?.options?.map(tagOption => ({
            label: tagOption.label,
            backgroundColor: tagOption.theme?.backgroundColor,
          }));
          return (
            <View flex row wrap shrinkWrap>
              <Tags tags={tags} />
            </View>
          );
        }}
      />
    );
  };

  return (
    <MBWorkflowDefaultComponentOptions
      component={component}
      firstSectionContent={
        <>
          {isChecklistInListViewEnabled && (
            <View paddingHorizontal={3} paddingBottom={2} row>
              <MBIconV2 name="task" color={colors.foregroundActive} size={grid(3)} />
              <View fill centerVertically paddingLeft={1}>
                <MBTextBody1>{i18n('workflowTemplate.component.tag.checkboxEnabled')}</MBTextBody1>
              </View>
              {!areChecklistOptionsSatisfied && (
                <AnchoredTooltip
                  tooltip={i18n(
                    'workflowTemplate.component.tag.mustHaveExactlyTwoResponseOptionsToUseAsCheckbox',
                  )}
                >
                  <Switch value={false} disabled onValueChange={() => {}} />
                </AnchoredTooltip>
              )}
              {areChecklistOptionsSatisfied && (
                <Switch
                  value={isChecklistEnabled ?? false}
                  onValueChange={() => onChecklistEnabledToggle(!isChecklistEnabled)}
                />
              )}
            </View>
          )}
        </>
      }
      extraOptions={
        <>
          {!isAuditTag && (
            <View paddingHorizontal={3} paddingVertical={1} row>
              <MBIconV2 name="tag" color={colors.foregroundActive} size={grid(3)} />
              <View fill centerVertically paddingLeft={1}>
                <MBTextBody1>{i18n('workflowTemplate.allowTagEditing')}</MBTextBody1>
              </View>
              <Switch
                value={addAdditionalValues ?? false}
                onValueChange={() => setAddAdditionalValues(!addAdditionalValues)}
              />
            </View>
          )}
        </>
      }
      extraSections={({ closeSubmenus }) => (
        <>
          <View paddingHorizontal={3} paddingVertical={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {i18n('workflowTemplate.responseOptions')}
            </MBTextOverlineBold>
          </View>
          <RCSubMenu
            key="options"
            style={{ zIndex: 999 }}
            popupClassName={styles.tagValuesMenu}
            title={
              <MBView
                style={cursorPointerStyle}
                row
                centerVertically
                paddingHorizontal={2}
                paddingVertical={1}
              >
                <View flex row wrap shrinkWrap>
                  {tagOptionsPreview}
                </View>
                <MBIconV2 name="arrow-point-right" color={colors.foregroundActive} size={grid(3)} />
              </MBView>
            }
          >
            <View style={{ width: 'fit-content' }}>
              {!isChecklistEnabled && (
                <View paddingHorizontal={3} paddingVertical={2}>
                  <MBTextOverlineBold color={colors.foregroundInactive}>
                    {i18n('workflowTemplate.responseOptions')}
                  </MBTextOverlineBold>
                </View>
              )}
              <MBScrollView style={[{ maxHeight: 288 }]}>
                <>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="options" renderClone={renderDraggableOptionRow}>
                      {provided => (
                        <div ref={provided.innerRef}>
                          {options.map((option, index) => (
                            <View key={option.value}>
                              <Draggable draggableId={option.value} index={index}>
                                {renderDraggableOptionRow}
                              </Draggable>
                            </View>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <MBTouchableOpacity onPress={() => setShowArchived(!showArchived)}>
                    <View paddingHorizontal={3} paddingVertical={2} centerVertically row>
                      <MBTextOverlineBold color={colors.foregroundInactive}>
                        {i18n('workflowTemplate.archived', { count: archivedOptions?.length })}
                      </MBTextOverlineBold>
                      <View fill />
                      <MBAnimation
                        duration={200}
                        easing={Easing.inOut(Easing.quad)}
                        disabled={!showArchived}
                        rotate={{ from: '0deg', to: '-180deg' }}
                      >
                        <MBIconV2
                          color={colors.foregroundActive}
                          name="arrow-point-down"
                          size={grid(3)}
                        />
                      </MBAnimation>
                    </View>
                  </MBTouchableOpacity>
                  {showArchived &&
                    archivedOptions?.map((archivedOption, index) => (
                      <TagOptionRow
                        key={index}
                        tagForegroundColor={colors.input.label}
                        onPressArchive={() => onUnarchiveOption(archivedOption.value)}
                      >
                        <View fill paddingHorizontal={1}>
                          <TextInput
                            value={archivedOption.label}
                            style={{
                              backgroundColor: colors.backgroundInactive,
                              borderRadius: 8,
                              padding: grid(1),
                              fontStyle: 'italic',
                            }}
                            editable={false}
                          />
                        </View>
                      </TagOptionRow>
                    ))}
                </>
              </MBScrollView>

              {canAddOption && (
                <View paddingHorizontal={1} centerVertically>
                  <MBTouchableOpacity onPress={onAddOption}>
                    <View row centerVertically paddingVertical={1} paddingLeft={1}>
                      <MBIconV2 size={grid(3)} name="plus" color={colors.foregroundActive} />
                      <View paddingLeft={1} />
                      <MBTextBody1 color={colors.foregroundActive}>
                        {i18n('card.blocks.tagsInput.createNewTagLabel')}
                      </MBTextBody1>
                    </View>
                  </MBTouchableOpacity>
                </View>
              )}
            </View>
          </RCSubMenu>
          <RCSubMenu
            key="tagSets"
            style={{ zIndex: 999, padding: 0 }}
            title={
              <MBMenuRow
                style={cursorPointerStyle}
                title={i18n('workflowTemplate.tagSetOptions')}
                paddingVertical={1}
                paddingRight={2}
                image={<MBIconV2 name="tag" color={colors.foregroundActive} size={grid(3)} />}
                skipWrappingIcon
                icon={
                  <MBIconV2
                    name="arrow-point-right"
                    color={colors.foregroundActive}
                    size={grid(3)}
                  />
                }
              />
            }
          >
            {tagGroupSelector({ onFinishSelectGroup: closeSubmenus })}
          </RCSubMenu>
          <View paddingHorizontal={2} paddingTop={1}>
            <Divider />
          </View>
          <View paddingHorizontal={3} paddingVertical={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {i18n('builder.defaultRow.rowHeader')}
            </MBTextOverlineBold>
          </View>
          <RCSubMenu
            key="defaultValues"
            style={{ zIndex: 999 }}
            title={<ComponentDefaultItemRow component={component} />}
          >
            <DefaultSelection workflowTemplateId={workflowTemplateId} component={component} />
          </RCSubMenu>
          {!isAuditTag && (
            <>
              <View paddingHorizontal={3} paddingVertical={1}>
                <MBTextOverlineBold color={colors.foregroundInactive}>
                  {i18n('builder.firstCardOverrideRow.rowHeader')}
                </MBTextOverlineBold>
              </View>
              <RCSubMenu
                key="overrideFirstCardValue"
                style={{ zIndex: 999 }}
                title={
                  <ComponentDefaultItemRow
                    component={component}
                    overridePath="firstRecurringEntityDefault"
                  />
                }
              >
                <DefaultSelection
                  workflowTemplateId={workflowTemplateId}
                  component={component}
                  overridePath="firstRecurringEntityDefault"
                />
              </RCSubMenu>
              <View paddingHorizontal={3} paddingVertical={1}>
                <MBTextOverlineBold color={colors.foregroundInactive}>
                  {i18n('builder.upcomingCardOverrideRow.rowHeader')}
                </MBTextOverlineBold>
              </View>
              <RCSubMenu
                key="overrideUpcomingCardValue"
                style={{ zIndex: 999 }}
                title={
                  <ComponentDefaultItemRow
                    component={component}
                    overridePath="upcomingRecurringEntityDefault"
                  />
                }
              >
                <DefaultSelection
                  workflowTemplateId={workflowTemplateId}
                  component={component}
                  overridePath="upcomingRecurringEntityDefault"
                />
              </RCSubMenu>
            </>
          )}
        </>
      )}
    />
  );
}

interface TagsProps {
  tags: Partial<API.TagComponentOption>[];
}

function Tags({ tags }: TagsProps) {
  return (
    <>
      {tags.map(tag => (
        <Tag
          key={tag.value}
          color={tag.backgroundColor as Color}
          textStyle={MBTypographyStyles.body2}
          label={tag.label ?? ''}
        />
      ))}
    </>
  );
}
